/**
 * Ticket-based Guest Login Component for Main Site
 *
 * Allows guests with meal tickets to login using ticket codes.
 * Simplified version without QR scanner (desktop/web focused).
 */

import { useState } from 'react';
import { useValidateTicket } from '../../hooks/useTickets';
import { useAuth } from './AuthProvider';

interface TicketGuestLoginProps {
  onSuccess?: () => void;
}

export function TicketGuestLogin({ onSuccess }: TicketGuestLoginProps) {
  const [ticketCode, setTicketCode] = useState('HMH-');
  const [guestName, setGuestName] = useState('');
  const [step, setStep] = useState<'ticket' | 'name' | 'already-used'>('ticket');
  const [errorMessage, setErrorMessage] = useState('');
  const [redeemedInfo, setRedeemedInfo] = useState<{ date: string; name?: string } | null>(null);

  const validateTicket = useValidateTicket();
  const { loginWithTicket, isLoading: authLoading } = useAuth();

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!ticketCode.trim()) {
      setErrorMessage('Please enter a ticket code');
      return;
    }

    try {
      const result = await validateTicket.mutateAsync(ticketCode.trim().toUpperCase());

      if (result.valid) {
        // Valid ticket - proceed to name entry
        setStep('name');
      } else {
        // Check if ticket was already redeemed
        if (result.message?.includes('already redeemed')) {
          setRedeemedInfo({
            date: result.message.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)?.[0] || 'previously',
            name: result.redeemed_by
          });
          setStep('already-used');
        } else {
          // Other validation errors (expired, voided, etc.)
          setErrorMessage(result.message || 'Invalid ticket');
        }
      }
    } catch (error: any) {
      // Handle network or other errors
      const errorText = error.message || 'Failed to validate ticket';
      if (errorText.includes('already redeemed')) {
        setStep('already-used');
      } else {
        setErrorMessage(errorText);
      }
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) {
      return;
    }

    const result = await loginWithTicket(ticketCode.trim().toUpperCase(), guestName.trim());

    if (result.success) {
      onSuccess?.();
    } else {
      setErrorMessage(result.message || 'Login failed');
      // Go back to ticket entry
      setStep('ticket');
      setTicketCode('HMH-');
      setGuestName('');
    }
  };

  const handleTryAgain = () => {
    setTicketCode('HMH-');
    setGuestName('');
    setStep('ticket');
    setErrorMessage('');
    setRedeemedInfo(null);
  };

  // Already Used Screen
  if (step === 'already-used') {
    return (
      <div className="space-y-4">
        {/* Friendly message box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <div className="w-12 h-12 bg-amber-200 rounded-full mx-auto mb-3 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-amber-900 mb-1">
            Ticket Already Used
          </h3>

          <p className="text-sm text-amber-800 mb-1">
            This meal ticket has already been redeemed.
          </p>

          {redeemedInfo && (
            <div className="text-sm text-amber-700 mt-2">
              {redeemedInfo.name && (
                <p>Used by: <span className="font-semibold">{redeemedInfo.name}</span></p>
              )}
              {redeemedInfo.date !== 'previously' && (
                <p className="text-xs mt-1">On: {redeemedInfo.date}</p>
              )}
            </div>
          )}
        </div>

        {/* Ticket code display */}
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-600 mb-1">Ticket Code</p>
          <p className="text-base font-mono font-bold text-gray-900">{ticketCode}</p>
        </div>

        {/* Action button */}
        <button
          onClick={handleTryAgain}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          Try a Different Ticket
        </button>

        <p className="text-xs text-gray-500 text-center">
          Please check with staff if you believe this is an error.
        </p>
      </div>
    );
  }

  // Name Entry Screen
  if (step === 'name') {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-green-700">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">Valid Ticket</span>
          </div>
          <p className="text-sm text-green-600 mt-1 font-mono">{ticketCode}</p>
        </div>

        <form onSubmit={handleNameSubmit} className="space-y-4">
          <div>
            <label htmlFor="guest-name" className="block text-sm font-medium text-gray-700 mb-1">
              Please enter your name
            </label>
            <input
              id="guest-name"
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoComplete="off"
              autoFocus
              required
              disabled={authLoading}
            />
          </div>

          {errorMessage && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={!guestName.trim() || authLoading}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {authLoading ? 'Signing In...' : 'Continue'}
          </button>

          <button
            type="button"
            onClick={handleTryAgain}
            disabled={authLoading}
            className="w-full py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Use Different Ticket
          </button>
        </form>
      </div>
    );
  }

  // Ticket Entry Screen
  return (
    <div className="space-y-4">
      <form onSubmit={handleTicketSubmit} className="space-y-4">
        <div>
          <label htmlFor="ticket-code" className="block text-sm font-medium text-gray-700 mb-1">
            Ticket Code
          </label>
          <input
            id="ticket-code"
            type="text"
            value={ticketCode}
            onChange={(e) => {
              let val = e.target.value.toUpperCase();
              // Ensure HMH- prefix is always present
              if (!val.startsWith('HMH-')) {
                val = 'HMH-' + val.replace(/^HMH-?/i, '');
              }
              setTicketCode(val);
            }}
            placeholder="HMH-XXXXXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono tracking-wide"
            autoComplete="off"
            autoFocus
            disabled={validateTicket.isPending}
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter the 6-character code after HMH-
          </p>
        </div>

        {errorMessage && (
          <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={!ticketCode.trim() || validateTicket.isPending}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          {validateTicket.isPending ? 'Validating...' : 'Validate Ticket'}
        </button>
      </form>

      <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
        Guests with meal tickets can use this option to place orders
      </div>
    </div>
  );
}
